import type { RenderVariant, ResolvedEdit, SourcePack } from "@desk/core";
import { buildEditedTimeline } from "../../../shell/lib/timeline.js";

/**
 * One edit-order segment whose EDIT range comes from the projected edited
 * timeline (keep/omit/trim + reorder) but whose SOURCE (video-media) range is
 * the beat's authored hybrid `BeatMediaBinding` range — NOT the legacy semantic
 * `pack.beats` range. This is the canonical media-time mapping for the muted
 * hybrid clip (H1 hardening fix).
 */
export interface HybridTimelineSegment {
  beatId: string;
  /** Inclusive edited-timeline tick. */
  editStartTick: number;
  /** Exclusive edited-timeline tick. */
  editEndTick: number;
  /** Inclusive source (video) tick from the beat's hybrid binding. */
  srcStartTick: number;
  /** Exclusive source (video) tick from the beat's hybrid binding. */
  srcEndTick: number;
}

export interface HybridTimeline {
  segments: HybridTimelineSegment[];
  durationTicks: number;
}

/**
 * Build the binding-based hybrid timeline.
 *
 * Reuses {@link buildEditedTimeline} for the EDIT structure/order (which retained
 * beats appear, in which edit order, and their edited tick ranges), then
 * substitutes each segment's source range with the hybrid variant's
 * `BeatMediaBinding` range for that beatId. The edit ranges therefore reflect the
 * player's cuts/reorders, while the source ranges always point at the correct
 * media content for the beat.
 */
export function buildHybridTimeline(
  pack: SourcePack,
  resolved: ResolvedEdit,
  variant: RenderVariant,
): HybridTimeline {
  const edited = buildEditedTimeline(pack, resolved);
  const bindingByBeat = new Map<string, { start: number; end: number }>();
  for (const b of variant.beatBindings) {
    if (!bindingByBeat.has(b.beatId)) {
      bindingByBeat.set(b.beatId, { start: b.sourceStartTick, end: b.sourceEndTick });
    }
  }
  // Build a quick lookup for each beat's FULL pack source range so we can
  // proportionally map a trim (inTick/outTick within the beat) into the
  // binding's media range.  MF3 fix.
  const beatPackRange = new Map<string, { start: number; end: number }>();
  for (const b of pack.beats) {
    beatPackRange.set(b.id, { start: b.startTick, end: b.endTick });
  }
  const segments: HybridTimelineSegment[] = [];
  for (const seg of edited.segments) {
    const binding = bindingByBeat.get(seg.beatId);
    if (!binding) continue;
    // MF3: proportionally map the segment's pack source range [seg.sourceStartTick,
    // seg.sourceEndTick) into the beat's binding media range.  For an untrimmed beat
    // seg.sourceStartTick === beat.startTick and seg.sourceEndTick === beat.endTick,
    // so the fractions are 0 and 1 → identity (full binding range).  No H1 regression.
    const packRange = beatPackRange.get(seg.beatId);
    let srcStartTick: number;
    let srcEndTick: number;
    if (packRange && packRange.end > packRange.start) {
      const beatLen = packRange.end - packRange.start;
      const bindingLen = binding.end - binding.start;
      srcStartTick =
        binding.start + ((seg.sourceStartTick - packRange.start) / beatLen) * bindingLen;
      srcEndTick =
        binding.start + ((seg.sourceEndTick - packRange.start) / beatLen) * bindingLen;
    } else {
      // Degenerate beat with zero length: fall back to full binding range.
      srcStartTick = binding.start;
      srcEndTick = binding.end;
    }
    segments.push({
      beatId: seg.beatId,
      editStartTick: seg.editStartTick,
      editEndTick: seg.editEndTick,
      srcStartTick,
      srcEndTick,
    });
  }
  return { segments, durationTicks: edited.durationTicks };
}

/**
 * Map an EDIT tick to VIDEO time (seconds).
 *
 * Finds the retained beat (in edit order) the tick falls in, computes the tick's
 * proportional offset within that beat's EDIT range, and maps it onto the beat's
 * BINDING source range. Divides by `tickRate` to produce seconds.
 *
 * At a segment's edit-start the offset is 0, so the result is exactly
 * `binding.sourceStartTick / tickRate`.
 */
export function hybridEditTickToVideoTimeSec(
  timeline: HybridTimeline,
  editTick: number,
  tickRate: number,
): number {
  if (timeline.segments.length === 0) return 0;
  const clamped = Math.max(0, Math.min(editTick, Math.max(0, timeline.durationTicks - 1)));
  for (const seg of timeline.segments) {
    if (clamped >= seg.editStartTick && clamped < seg.editEndTick) {
      const editLen = seg.editEndTick - seg.editStartTick;
      const frac = editLen > 0 ? (clamped - seg.editStartTick) / editLen : 0;
      const videoTick = seg.srcStartTick + frac * (seg.srcEndTick - seg.srcStartTick);
      return videoTick / tickRate;
    }
  }
  const last = timeline.segments[timeline.segments.length - 1]!;
  return Math.max(last.srcStartTick, last.srcEndTick - 1) / tickRate;
}

/**
 * Inverse of {@link hybridEditTickToVideoTimeSec}: map a VIDEO time (seconds) back
 * to the nearest EDIT tick. Used to drive the scrubber from the native `<video>`
 * element's `timeupdate` during continuous real-time playback (H2 fix) — the
 * element runs in source time, and this projects that position onto the edited
 * timeline so the transport tick tracks playback.
 *
 * Finds the segment whose SOURCE range contains the video tick (ties broken by the
 * segment nearest `preferEditTick`, so a linearly-playing clip advances forward
 * through edit order), then maps proportionally back onto that segment's EDIT range.
 * Falls back to the closest segment boundary when the time is outside every range.
 */
export function hybridVideoTimeSecToEditTick(
  timeline: HybridTimeline,
  videoTimeSec: number,
  tickRate: number,
  preferEditTick = 0,
): number {
  if (timeline.segments.length === 0) return 0;
  const videoTick = videoTimeSec * tickRate;
  const maxEdit = Math.max(0, timeline.durationTicks - 1);

  let best: { editTick: number; dist: number } | null = null;
  for (const seg of timeline.segments) {
    const lo = Math.min(seg.srcStartTick, seg.srcEndTick);
    const hi = Math.max(seg.srcStartTick, seg.srcEndTick);
    if (videoTick >= lo && videoTick <= hi) {
      const srcLen = seg.srcEndTick - seg.srcStartTick;
      const frac = srcLen !== 0 ? (videoTick - seg.srcStartTick) / srcLen : 0;
      const editTick = seg.editStartTick + frac * (seg.editEndTick - seg.editStartTick);
      const dist = Math.abs(editTick - preferEditTick);
      if (!best || dist < best.dist) best = { editTick, dist };
    }
  }
  if (best) return Math.max(0, Math.min(maxEdit, Math.round(best.editTick)));

  // Outside every segment's source range — snap to the closest segment edge.
  let closest = timeline.segments[0]!;
  let closestDist = Infinity;
  for (const seg of timeline.segments) {
    const mid = (seg.srcStartTick + seg.srcEndTick) / 2;
    const d = Math.abs(videoTick - mid);
    if (d < closestDist) {
      closestDist = d;
      closest = seg;
    }
  }
  const snap = videoTick <= closest.srcStartTick ? closest.editStartTick : closest.editEndTick - 1;
  return Math.max(0, Math.min(maxEdit, Math.round(snap)));
}
